import React from 'react';
import { RangeSlider } from './RangeSlider';
import { Assumptions, CompModel } from '../types/compensation';
import { formatDkk } from '../utils/format';
interface LeverPanelProps {
  model: CompModel;
  assumptions: Assumptions;
  onFixedChange: (value: number) => void;
  onShareChange: (value: number) => void;
}
export function LeverPanel({
  model,
  assumptions,
  onFixedChange,
  onShareChange
}: LeverPanelProps) {
  const commissionPct = Math.round(model.commissionShare * 100);
  const equityPct = 100 - commissionPct;
  return <section aria-label="Compensation levers" className="rounded-card border border-line bg-ink-900 p-6 lg:p-8" data-id="element-52" data-mp="a4rspmfswhmw">
      <div data-id="element-53" data-mp="ow0ipwnm850k">
        <div className="flex items-baseline justify-between gap-4" data-id="element-54" data-mp="jvcrb9vh5m5t">
          <label htmlFor="fixed-salary" className="text-[11px] font-medium uppercase tracking-[0.18em] text-security" data-id="element-55" data-mp="jx7c8o6qxe9w">
            1 · Fixed monthly salary
          </label>
          <span className="tnum text-lg font-semibold text-paper" data-id="element-56" data-mp="sty4epi6g8z7">
            {formatDkk(model.fixedMonthly)}
          </span>
        </div>
        <div className="mt-4" data-id="element-57" data-mp="325d3ao0n7rgs">
          <RangeSlider id="fixed-salary" label="Fixed monthly salary" min={assumptions.minFixedSalary} max={assumptions.maxFixedSalary} step={1000} value={model.fixedMonthly} accent="#7FA3C4" onChange={onFixedChange} ariaValueText={`${formatDkk(model.fixedMonthly)} per month`} data-id="element-58" data-mp="326oeva0by3ig" />
        </div>
        <div className="tnum flex justify-between text-xs text-paper-dim" data-id="element-59" data-mp="3l37gle8t0p4z">
          <span data-id="element-60" data-mp="3px1z4ecoay2h">{formatDkk(assumptions.minFixedSalary)}</span>
          <span data-id="element-61" data-mp="3uuk8u6yilhbj">{formatDkk(assumptions.maxFixedSalary)}</span>
        </div>
        <div className="tnum mt-5 flex items-baseline justify-between gap-4 border-t border-line-soft pt-4 text-sm" data-id="element-62" data-mp="3uwobymolacar">
          <span className="text-paper-dim" data-id="element-63" data-mp="7puns51c5aym">Annual pool to allocate</span>
          <span className="font-medium text-paper" data-id="element-64" data-mp="7o1dmfrik8lv">
            {formatDkk(model.annualSacrifice)}
          </span>
        </div>
      </div>

      <div className="mt-10 border-t border-line-soft pt-8" data-id="element-65" data-mp="1zsgkniv6472r">
        <div className="flex items-baseline justify-between gap-4" data-id="element-66" data-mp="1zpucpi4d7pk0">
          <label htmlFor="allocation" className="text-[11px] font-medium uppercase tracking-[0.18em] text-paper-dim" data-id="element-67" data-mp="2dkn17628gg2q">
            2 · Allocate the pool
          </label>
          <span className="text-sm text-paper-muted" data-id="element-68" data-mp="2nfosa7g2js7y">One krone, one place</span>
        </div>

        <div className="mt-4 flex items-end justify-between gap-4" data-id="element-69" data-mp="2ihn5pcjfparq">
          <div data-id="element-70" data-mp="2wi7sejsgzqqi">
            <p className="tnum text-2xl font-semibold text-performance" data-id="element-71" data-mp="2wgmwoquf61r2">
              {commissionPct}%
            </p>
            <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-paper-dim" data-id="element-72" data-mp="2sh4a7qmy9im4">
              Commission
            </p>
          </div>
          <div className="text-right" data-id="element-73" data-mp="y2l99si23n67">
            <p className="tnum text-2xl font-semibold text-ownership" data-id="element-74" data-mp="y3mu2oovjo14">
              {equityPct}%
            </p>
            <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-paper-dim" data-id="element-75" data-mp="nz6hc46oyn1j">
              Equity
            </p>
          </div>
        </div>

        <div className="mt-3" data-id="element-76" data-mp="e93hbk15k8tp">
          <RangeSlider id="allocation" label="Commission versus equity allocation" min={0} max={100} step={1} value={commissionPct} accent="#E2A03F" onChange={v => onShareChange(v / 100)} ariaValueText={`${commissionPct} percent commission, ${equityPct} percent equity`} data-id="element-77" data-mp="e7s8v6wricw9" />
        </div>

        <div className="tnum grid grid-cols-2 gap-4 border-t border-line-soft pt-4 text-sm" data-id="element-78" data-mp="3rgodhed3q5m4">
          <div data-id="element-79" data-mp="9c61r20t6je">
            <p className="text-paper-dim" data-id="element-80" data-mp="92c5sduuf8f">To commission</p>
            <p className="mt-1 text-paper" data-id="element-81" data-mp="1pngvtya0nbx2">{formatDkk(model.commissionCash)}</p>
          </div>
          <div className="text-right" data-id="element-82" data-mp="1un3ein8m5kd9">
            <p className="text-paper-dim" data-id="element-83" data-mp="1um1ye4cqq7oo">To equity</p>
            <p className="mt-1 text-paper" data-id="element-84" data-mp="3qq821rslrgi0">{formatDkk(model.equityCash)}</p>
          </div>
        </div>
      </div>
    </section>;
}